import { useEffect } from 'react'

import useBrand from './useBrand'
import useCurrentUser from './useCurrentUser'

type IntercomType = {
  hideWidget?: boolean,
  horizontalPadding?: number,
}

export type TrackEventParams = {
  eventName: string,
  eventPayload?: Record<string, string | boolean>,
}

const useIntercom = (options: IntercomType = {}) => {
  // TODO: When the intercom flipper key is removed, default the hideWidget option to false
  const { hideWidget, horizontalPadding = 20 } = options

  function loadIntercom(appId: string, settings: Record<string, unknown> = {}) {
    if (typeof window === 'undefined') return

    if (window.Intercom && typeof window.Intercom === 'function'){
      window.Intercom('update', settings)
      return
    }

    window.intercomSettings = { app_id: appId, ...settings }

    const scriptId = 'intercom-script'
    if (document.getElementById(scriptId)) return

    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(script)
  }

  const {
    currentUser,
    isBrandUser, agency, roleTitle, loaded,
  } = useCurrentUser()

  const {
    brand_id, email, full_name, id, mobile_number, cache_key,
  } = currentUser

  const intercomUserJwt = (currentUser as Record<string, unknown>)?.intercom_user_jwt as string | undefined
  const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID
  const isReady = Boolean(id && intercomUserJwt && intercomAppId)

  const { brand: brandEntity } = useBrand({ id: brand_id })

  useEffect(() => {
    if (!isReady || hideWidget){
      return
    }

    const settings = {
      app_id: intercomAppId,
      brand: brandEntity?.title,
      company: {
        id: agency?.id?.toString() || `unknown-${id}`,
        name: agency?.title || 'Unknown Company',
        industry: 'Real Estate',
        plan: 'N/A',
        user_count: 'N/A',
      },
      custom_attributes: {
        business_unit: 'Realhub',
        onboarding: false,
      },
      email,
      horizontal_padding: horizontalPadding,
      intercom_user_jwt: intercomUserJwt,
      name: full_name,
      phone: mobile_number,
      role: isBrandUser ? 'Brand User' : roleTitle,
      user_id: String(id),
      z_index: 997,
    }

    loadIntercom(intercomAppId!, settings)
  }, [
    agency?.id,
    agency?.title,
    brandEntity?.title,
    cache_key,
    hideWidget,
    horizontalPadding,
    id,
    intercomAppId,
    intercomUserJwt,
    isBrandUser,
    isReady,
    loaded,
    email,
    full_name,
    mobile_number,
    roleTitle,
  ])

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!window.Intercom || hideWidget) return

      const target = event.target as HTMLElement | null
      if (!target) return

      const clickedInsideIntercom = !!target.closest('[id^="intercom"], [class*="intercom"]')

      if (!clickedInsideIntercom){
        window.Intercom?.('hide')
      }
    }

    document.addEventListener('click', onDocClick, { capture: true, passive: true })
    return () => document.removeEventListener('click', onDocClick, { capture: true })
  }, [hideWidget])

  const trackEvent = (params: TrackEventParams) => {
    const { eventName, eventPayload = {} } = params
    if (window.Intercom){
      const extendedPayload = {
        ...eventPayload,
        agency: agency?.title,
        business_unit: 'Engage',
        role: isBrandUser ? 'Brand User' : roleTitle,
        user_id: id || 'Unknown User',
      }
      window.Intercom('trackEvent', eventName, extendedPayload)
    }
  }

  return {
    callbacks: {
      trackEvent: (params: TrackEventParams) => {
        trackEvent(params)
      },
    },
  }
}

export default useIntercom
